/**
 * Unique source for the main tab bar + desktop sidebar.
 *
 * To add a tab: append an entry here, add a MainTabs child route with the
 * same `path` as `id`, use page selector `re-${id}`, and register a new
 * ionicon in `app.component.ts` if it is not already in `addIcons`.
 */
export type MainTabDefinition = {
  id: string;
  icon: string;
  labelKey: string;
  /** Sidebar `routerLinkActive` exact match (home). Prefix match otherwise. */
  exact?: boolean;
};

export const MAIN_TABS = [
  {
    id: 'home',
    icon: 'home',
    labelKey: 'nav.home',
    exact: true,
  },
  {
    id: 'stories',
    icon: 'book-outline',
    labelKey: 'nav.stories',
    exact: false,
  },
  {
    id: 'characters',
    icon: 'people-outline',
    labelKey: 'nav.characters',
    exact: false,
  },
] as const satisfies readonly MainTabDefinition[];

export type MainTabId = (typeof MAIN_TABS)[number]['id'];

export const MAIN_TAB_IDS: readonly MainTabId[] = MAIN_TABS.map(
  (tab) => tab.id,
);

export const MAIN_TAB_DEFAULT_ID: MainTabId = MAIN_TABS[0].id;

export function mainTabLink(id: string): string {
  return `/${id}`;
}

/**
 * Longest prefix among `/${id}` and `/${id}/…`. Unknown URLs fall back to
 * the first tab so the indicator never points at a missing button.
 */
export function pathToTabId(
  url: string,
  tabIds: readonly string[] = MAIN_TAB_IDS,
): string {
  const path = url.split(/[?#]/, 1)[0] ?? '';
  let matched: string | undefined;
  let matchedLen = -1;
  for (const id of tabIds) {
    const prefix = `/${id}`;
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      if (prefix.length > matchedLen) {
        matched = id;
        matchedLen = prefix.length;
      }
    }
  }
  return matched ?? tabIds[0] ?? MAIN_TAB_DEFAULT_ID;
}

export function mainTabFromUrl(url: string): MainTabId {
  const id = pathToTabId(url, MAIN_TAB_IDS);
  return MAIN_TAB_IDS.includes(id as MainTabId)
    ? (id as MainTabId)
    : MAIN_TAB_DEFAULT_ID;
}
